import axios from 'axios';
import { APP_TOKEN } from '../constants';

export const normalizePermissionName = (value) => {
  if (!value) return '';
  return `${value}`.trim().replace(/\s+/g, '_').toUpperCase();
};

export const normalizeRoleName = (value) => {
  if (!value) return '';
  return `${value}`
    .trim()
    .replace(/[\s_-]+/g, '')
    .toUpperCase();
};

export const collectPermissionNames = (authData) => {
  const permissionNames = new Set();

  const directPermissions = [
    ...(Array.isArray(authData?.permissions) ? authData.permissions : []),
    ...(Array.isArray(authData?.user?.permissions)
      ? authData.user.permissions
      : []),
  ];
  directPermissions.forEach((permission) => {
    const normalized = normalizePermissionName(
      permission?.permissionName || permission?.name || permission,
    );
    if (normalized) permissionNames.add(normalized);
  });

  const roles = [
    ...(Array.isArray(authData?.roles) ? authData.roles : []),
    ...(Array.isArray(authData?.user?.roles) ? authData.user.roles : []),
    ...(authData?.role ? [authData.role] : []),
  ];
  roles.forEach((role) => {
    const rolePermissions = Array.isArray(role?.permissions)
      ? role.permissions
      : [];
    rolePermissions.forEach((permission) => {
      const normalized = normalizePermissionName(
        permission?.permissionName || permission?.name || permission,
      );
      if (normalized) permissionNames.add(normalized);
    });
  });

  return permissionNames;
};

export const hasAnyPermission = (permissionNames, candidates = []) => {
  if (!permissionNames || permissionNames.size === 0) return false;
  const normalizedCandidates = candidates
    .map(normalizePermissionName)
    .filter(Boolean);
  if (normalizedCandidates.length === 0) return false;

  const names = Array.from(permissionNames);
  return normalizedCandidates.some((candidate) => {
    if (candidate.endsWith('*')) {
      const prefix = candidate.slice(0, -1);
      return names.some((name) => name.startsWith(prefix));
    }
    return names.some((name) => name === candidate || name.includes(candidate));
  });
};

export const syncUserPermissions = async (currentAuthData, setAuthData) => {
  try {
    const authData =
      currentAuthData ||
      (typeof window === 'undefined'
        ? null
        : JSON.parse(window.localStorage.getItem(APP_TOKEN) || 'null'));

    const token = authData?.token;
    if (!token) return null;

    const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
    if (!graphqlEndpoint) return null;

    const rolesQuery = {
      query: `query {
        roles {
          id
          name
          permissions {
            id
            name
            permissionName
          }
        }
      }`,
    };

    const response = await axios.post(graphqlEndpoint, rolesQuery, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200 && response.data?.data?.roles) {
      const allRoles = response.data.data.roles;
      const userRoles = [
        ...(Array.isArray(authData?.roles) ? authData.roles : []),
        ...(Array.isArray(authData?.user?.roles) ? authData.user.roles : []),
        ...(authData?.role ? [authData.role] : []),
      ];

      const userRoleNames = new Set(
        userRoles
          .map((r) => normalizeRoleName(typeof r === 'string' ? r : r?.name))
          .filter(Boolean),
      );
      const userRoleIds = new Set(
        userRoles.map((r) => r?.id && `${r.id}`).filter(Boolean),
      );

      const matchedRoles = allRoles.filter((r) => {
        const norm = normalizeRoleName(r?.name);
        const idStr = r?.id && `${r.id}`;
        return (
          (norm && userRoleNames.has(norm)) ||
          (idStr && userRoleIds.has(idStr))
        );
      });

      let collectedPerms = [];
      const seenNames = new Set();

      matchedRoles.forEach((role) => {
        (role.permissions || []).forEach((p) => {
          const permName = p?.permissionName || p?.name;
          const normalized = normalizePermissionName(permName);
          if (normalized && !seenNames.has(normalized)) {
            seenNames.add(normalized);
            collectedPerms.push(p);
          }
        });
      });

      // Preserve existing permissions only if no role matched
      if (matchedRoles.length === 0) {
        collectedPerms = Array.isArray(authData?.permissions)
          ? authData.permissions
          : [];
      }

      const nextAuth = {
        ...authData,
        roles: matchedRoles.length ? matchedRoles : userRoles,
        permissions: collectedPerms,
      };

      if (typeof setAuthData === 'function') {
        setAuthData(nextAuth);
      }
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(APP_TOKEN, JSON.stringify(nextAuth));
        window.dispatchEvent(
          new CustomEvent('auth:permissions-updated', { detail: nextAuth }),
        );
      }

      return nextAuth;
    }
  } catch (err) {
    console.error('Failed to sync user permissions:', err);
  }
  return null;
};
